import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";
import { waitForUploadStatus } from "../../../services/uploadStatus";

/* Building the outline.
 *
 * Triggers the analysis that already exists — POST /folders/<id>/analyze/ —
 * and watches the same worker the proposal page watches, over the same socket
 * helper. Nothing new is computed here; this is the wait, made legible.
 *
 * THE PERCENTAGE IS THE WORKER'S OWN. The task emits real milestones (5, 15,
 * 30, 55, 65, 75, 100) tied to actual stages, so the bar is determinate because
 * there is a genuine number to show — not because a determinate bar looks
 * better. The stage labels below are matched to those milestones rather than
 * advanced on a timer, and the message the worker sends is shown verbatim
 * underneath, because it is more specific than any label I could pick.
 *
 * RE-ATTACHING IS THE NORMAL CASE. Analysis runs for minutes; a user who
 * reloads or comes back gets `reattached: true` and the worker's LAST frame,
 * so the card resumes at 45% rather than resetting to 0 and looking restarted.
 */

const STAGES = [
  { at: 0, label: "Reading the RFP" },
  { at: 30, label: "Shaping the win strategy" },
  { at: 55, label: "Laying out the sections" },
];

const stageIndex = (pct) => {
  let i = 0;
  STAGES.forEach((s, n) => {
    if (pct >= s.at) i = n;
  });
  return i;
};

const ProposalPlanCard = ({ message, onReady, onFailed }) => {
  const { projectId, projectName } = message;

  const [pct, setPct] = useState(5);
  const [note, setNote] = useState("Starting…");
  const [error, setError] = useState(null);
  const startedRef = useRef(false);
  const waitRef = useRef(null);

  useEffect(() => {
    // The analysis is expensive; a second render must not start a second one.
    if (startedRef.current) return undefined;
    startedRef.current = true;

    let alive = true;

    (async () => {
      try {
        const orgId = Cookies.get("selected_org_id");
        const url = orgId
          ? `/api/v1/rfp/folders/${projectId}/analyze/?organization_id=${encodeURIComponent(orgId)}`
          : `/api/v1/rfp/folders/${projectId}/analyze/`;
        const res = await api.post(url, {});
        if (!alive) return;

        // 200 means the analysis was already cached — there is nothing to wait for.
        if (res.status !== 202) {
          setPct(100);
          onReady?.({ projectId, projectName });
          return;
        }

        // Seed from the worker's last frame when re-attaching, or a run genuinely
        // at 45% looks like it just started.
        const last = res.data?.last_status;
        if (typeof last?.progress === "number") setPct(last.progress);
        if (last?.message) setNote(last.message);

        const wait = waitForUploadStatus({
          statusKey: res.data?.status_key,
          timeoutMs: 10 * 60 * 1000,
          onProgress: (d) => {
            if (!alive) return;
            if (typeof d.progress === "number") {
              // Never let the bar go backwards. Frames can arrive out of order,
              // and a bar that retreats reads as work being undone.
              setPct((p) => Math.max(p, d.progress));
            }
            if (d.message) setNote(d.message);
          },
          onError: (d) => {
            if (!alive) return;
            setError(d?.message || "The outline couldn't be built. Check the documents and try again.");
            onFailed?.();
          },
        });
        waitRef.current = wait;

        const ok = await wait.promise;
        if (!alive) return;
        if (ok && !error) {
          setPct(100);
          // Hold the full bar for a beat so the fill is seen to finish rather
          // than being replaced mid-stride.
          setTimeout(() => alive && onReady?.({ projectId, projectName }), 420);
        }
      } catch {
        if (alive) {
          setError("Couldn't start building the outline.");
          onFailed?.();
        }
      }
    })();

    return () => {
      alive = false;
      // The socket outlives this component otherwise.
      waitRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const active = stageIndex(pct);

  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h16M4 12h10M4 18h13" />
        </svg>
      }
      title="Building the outline"
      meta={
        !error ? (
          <span className="text-[11.5px] font-semibold tabular-nums text-[#8E4187]">{Math.round(pct)}%</span>
        ) : null
      }
    >
      {error ? (
        <p role="alert" className="px-4 py-3.5 text-[13px] leading-relaxed text-[#B91C1C]">
          {error}
        </p>
      ) : (
        <div className="px-4 py-3.5">
          <ol className="space-y-2">
            {STAGES.map((s, i) => {
              const done = i < active || pct >= 100;
              const now = i === active && pct < 100;
              return (
                <li key={s.label} className="flex items-center gap-2.5">
                  <span className="relative flex h-3 w-3 flex-none items-center justify-center">
                    {done ? (
                      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1F7D38" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : now ? (
                      <>
                        <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-[#A04A97] opacity-50 motion-reduce:animate-none" />
                        <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#A04A97]" />
                      </>
                    ) : (
                      <span className="h-[5px] w-[5px] rounded-full bg-[#D8D3DC]" />
                    )}
                  </span>
                  <span
                    className={`text-[13px] leading-snug ${
                      now ? "font-semibold text-[#18101C]" : done ? "text-[#6B6472]" : "text-[#9C96A2]"
                    }`}
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>

          <div aria-hidden="true" className="mt-3 h-[4px] overflow-hidden rounded-full bg-[#18101C]/[0.06]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#A04A97] to-[#C86B4F] transition-[width] duration-700 ease-out motion-reduce:transition-none"
              style={{ width: `${Math.max(pct, 4)}%` }}
            />
          </div>

          {/* The worker's own words. More specific than any label above. */}
          <p role="status" className="mt-2 truncate text-[12px] text-[#6B6472]" title={note}>
            {note}
          </p>
          <p className="mt-2 border-t border-[#18101C]/[0.06] pt-2 text-[12px] text-[#6B6472]">
            You'll approve the outline before anything is written.
          </p>
        </div>
      )}
    </Shell>
  );
};

export default ProposalPlanCard;
